import { useEffect, useState } from 'react';
import { collection, limit, onSnapshot, or, query, where } from 'firebase/firestore';
import { useFirestore } from '../../helpers/firebaseContext';
import type { FirebaseTransaction } from '../../types';
import { securityKccNumber } from '../../../../shared/securityKcc';
import { findContractSecurity } from '../../../../shared/securityContractCatalogue';

export function useTransactionKccReadiness(transaction: FirebaseTransaction | undefined, enabled: boolean) {
    const db = useFirestore();
    const key = JSON.stringify((transaction?.securities ?? []).map(row => [row.value?.CODE ?? '', row.value?.EXCHANGE_CODE ?? '']));
    const [state, setState] = useState<{ key: string; loading: boolean; missing: string[] }>({ key: '', loading: true, missing: [] });
    useEffect(() => {
        if (!enabled) return;
        const identities = JSON.parse(key) as [string, string][];
        setState({ key, loading: true, missing: [] });
        if (!identities.length) { setState({ key, loading: false, missing: ['No securities selected'] }); return; }
        let active = true;
        const checks = new Map<number, boolean>();
        const update = () => {
            if (active) setState({ key, loading: checks.size < identities.length,
                missing: identities.filter((_, i) => checks.get(i) === false).map(([code, exchange]) => `${code || 'Unknown security'} (${exchange})`) });
        };
        const unsubscribers = identities.map(([code, exchange], index) => {
            if (!code || !exchange) { checks.set(index, false); update(); return () => {}; }
            // KFHTrade's KSE symbol can differ from Geneva's numeric CODE and BKM/BKP exchange.
            // Fetch all identity candidates so the shared resolver can reject ambiguous matches.
            const catalogueQuery = exchange === 'KSE'
                ? query(collection(db, 'securities'), or(where('CODE', '==', code), where('TICKER', '==', code), where('RICCODE', '==', `${code}.KW`)))
                : query(collection(db, 'securities'), where('CODE', '==', code), where('EXCHANGE_CODE', '==', exchange), limit(2));
            return onSnapshot(catalogueQuery, snapshot => {
                const security = findContractSecurity({ CODE: code, EXCHANGE_CODE: exchange }, snapshot.docs.map(doc => doc.data()));
                checks.set(index, !!securityKccNumber(security?.KCC_NUMBER)); update();
            }, () => { checks.set(index, false); update(); });
        });
        return () => { active = false; unsubscribers.forEach(unsubscribe => unsubscribe()); };
    }, [db, key, enabled]);
    const loading = enabled && (state.key !== key || state.loading);
    return { loading, missing: enabled && state.key === key ? state.missing : [], ready: !enabled || (!loading && state.missing.length === 0) };
}
